import { useState } from 'react'
import {
    Check, Crown, Home, MicOff, Plus, Radio, RotateCcw, Shield, ShieldMinus,
    ShieldPlus, Trash2, UserMinus, Users, Video, Volume2, X
} from 'lucide-react'
import { MriInput, cn } from '@mriqbox/ui-kit'
import {
    MAX_CHANNEL_NAME, MAX_CHANNELS, canModerate, isInVoice,
    type BanEntry, type Channel, type Participant, type ShareKind
} from '@shared/protocol'
import { Avatar } from '@/components/Avatar'
import { CopyButton } from '@/components/CopyField'
import { DiscordVoicePanel } from '@/components/DiscordVoicePanel'
import { roomLinkFor } from '@/lib/format'
import type { ShareQuality, ShareResolution } from '@/lib/screenCapture'
import type { EncoderChoice } from '@/lib/videoEncoder'
import type { UploadLimit } from '@/lib/uploadLimit'
import type { ReceivePreference } from '@/lib/receiveQuality'
import type { BackgroundEffect } from '@/lib/videoEffects'

export interface RoomSidebarProps {
    code: string
    roomName?: string
    participants: Participant[]
    selfId: string | null
    isHost: boolean
    /** Host ou admin: e quem ve os botoes de moderacao e a lista de expulsos. */
    isModerator: boolean
    /** Quem foi expulso desta sala. Vazia pra quem nao modera. */
    bans: BanEntry[]
    /** Quem esta falando agora (medido no proprio navegador). */
    speaking: Set<string>
    /** Volume por pessoa, 0 a 1. */
    volumes: Record<string, number>
    /**
     * Etiqueta de qualidade ja pronta, por participante (`1080p60 · 4.2 Mbps`).
     *
     * Chega montada de cima porque quem MEDE e o tile — e o `<video>` que sabe
     * o tamanho do que chegou. A lista so exibe.
     */
    qualityByPeer?: Record<string, string>
    onVolumeChange: (peerId: string, volume: number) => void
    onRename: (nickname: string) => void
    onKick: (id: string) => void
    onSetAdmin: (id: string, admin: boolean) => void
    onUnban: (id: string) => void
    /**
     * Sub-salas da sala fixa. Ausente na sala do momento — ai a lista segue
     * plana, sem agrupamento nem botao de criar.
     */
    channels?: Channel[]
    /** Sub-sala em que estou (`null` = sala principal). */
    selfChannel?: string | null
    onJoinChannel?: (id: string | null) => void
    onCreateChannel?: (name: string) => void
    onDeleteChannel?: (id: string) => void
    // ── Discord Voice Panel Props ──
    sharingKinds: Set<ShareKind>
    canShareScreen: boolean
    canUseCamera: boolean
    quality: ShareQuality
    resolution: ShareResolution
    encoder: EncoderChoice
    encoderNote?: string | null
    cameraId: string | null
    background: BackgroundEffect
    effectLoading: boolean
    avatar?: string | null
    inVoice: boolean
    muted: boolean
    deafened: boolean
    screenHasAudio?: boolean
    screenAudioMuted?: boolean
    onToggleScreenAudio?: () => void
    voiceLoading: boolean
    micId: string | null
    noiseSuppression: boolean
    onToggleShare: (kind: ShareKind) => void
    onToggleVoice: () => void
    onToggleMute: () => void
    onToggleDeafen: () => void
    onMicChange: (deviceId: string) => void
    onCameraChange: (deviceId: string) => void
    onQualityChange: (quality: ShareQuality) => void
    onResolutionChange: (resolution: ShareResolution) => void
    onEncoderChange: (encoder: EncoderChoice) => void
    receivePreference?: ReceivePreference
    onReceivePreferenceChange?: (preference: ReceivePreference) => void
    /** The LIVE badge next to someone's name opens their stream. */
    onWatchScreen?: (peerId: string) => void
    uploadLimit?: UploadLimit
    onUploadLimitChange?: (limit: UploadLimit) => void
    onBackgroundChange: (effect: BackgroundEffect) => void
    onNoiseSuppressionChange: (enabled: boolean) => void
    onAvatarChange?: (dataUrl: string | null) => void
    onLeave: () => void
    /** Fecha a gaveta no celular. Ausente no desktop, onde ela e coluna fixa. */
    onClose?: () => void
    className?: string
}

export function RoomSidebar({
    onWatchScreen,
    code,
    roomName,
    participants,
    selfId,
    isHost,
    isModerator,
    bans,
    speaking,
    volumes,
    qualityByPeer,
    onVolumeChange,
    onRename,
    onKick,
    onSetAdmin,
    onUnban,
    channels,
    selfChannel = null,
    onJoinChannel,
    onCreateChannel,
    onDeleteChannel,
    sharingKinds,
    canShareScreen,
    canUseCamera,
    quality,
    resolution,
    encoder,
    encoderNote,
    cameraId,
    background,
    effectLoading,
    avatar,
    inVoice,
    muted,
    deafened,
    screenHasAudio,
    screenAudioMuted,
    onToggleScreenAudio,
    voiceLoading,
    micId,
    noiseSuppression,
    onToggleShare,
    onToggleVoice,
    onToggleMute,
    onToggleDeafen,
    onMicChange,
    onCameraChange,
    onQualityChange,
    onResolutionChange,
    onEncoderChange,
    receivePreference,
    onReceivePreferenceChange,
    uploadLimit,
    onUploadLimitChange,
    onBackgroundChange,
    onNoiseSuppressionChange,
    onAvatarChange,
    onLeave,
    onClose,
    className
}: RoomSidebarProps) {
    const self = participants.find((participant) => participant.id === selfId)
    const canalAtual = channels?.find((channel) => channel.id === selfChannel)

    const renderRow = (participant: Participant, outraSala = false) => (
        <ParticipantRow
            onWatchScreen={onWatchScreen}
            key={participant.id}
            participant={participant}
            isSelf={participant.id === selfId}
            selfIsHost={isHost}
            selfModerates={isModerator}
            speaking={speaking.has(participant.id)}
            quality={qualityByPeer?.[participant.id]}
            volume={volumes[participant.id] ?? 1}
            onVolumeChange={onVolumeChange}
            onKick={onKick}
            onSetAdmin={onSetAdmin}
            selfMuted={muted}
            selfDeafened={deafened}
            outraSala={outraSala}
        />
    )

    return (
        <aside className={cn('relative flex w-64 shrink-0 flex-col border-r border-border/70 bg-card/40 select-none', className)}>
            {/*
              * So no celular, onde este painel e uma gaveta por cima do video:
              * no desktop ele e coluna fixa e nao ha o que fechar.
              */}
            {onClose && (
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Fechar o painel da sala"
                    className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-lg border border-border/60 bg-card text-muted-foreground transition-colors hover:text-foreground md:hidden"
                >
                    <X className="h-4 w-4" />
                </button>
            )}

            <div className="p-2.5">
                <div className="rounded-xl border border-border/70 bg-card/70 p-2.5">
                    {roomName ? (
                        <p className="truncate text-sm font-bold text-foreground" title={roomName}>{roomName}</p>
                    ) : (
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                            Código da sala
                        </p>
                    )}
                    <div className="mt-1 flex items-center justify-between gap-2">
                        <span className="font-mono tracking-[0.18em] text-base font-bold text-foreground">{code}</span>
                        <CopyButton value={code} label="Copiar código" iconOnly variant="ghost" />
                    </div>
                    <CopyButton
                        value={roomLinkFor(code)}
                        label="Copiar link do convite"
                        className="mt-2 w-full text-xs h-8"
                    />
                </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col border-t border-border/70">
                <div className="flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    <span>Participantes</span>
                    <span className="rounded-full bg-muted px-1.5 py-0.2 text-[10px] font-bold">{participants.length}</span>
                </div>
                {channels ? (
                    <ChannelList
                        channels={channels}
                        participants={participants}
                        selfChannel={selfChannel}
                        canDelete={isModerator && !!onDeleteChannel}
                        onJoin={(id) => onJoinChannel?.(id)}
                        onDelete={(id) => onDeleteChannel?.(id)}
                        renderRow={renderRow}
                    />
                ) : (
                    <ul className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-1.5 pb-2">
                        {participants.map((participant) => renderRow(participant))}
                    </ul>
                )}

                {isModerator && bans.length > 0 && (
                    <BannedList entries={bans} onUnban={onUnban} />
                )}
            </div>

            {/*
              * Logo acima de "Voz conectada": e o lugar onde a pessoa ja esta
              * olhando quando pensa em pra onde levar a conversa. So pra quem
              * modera — sub-sala fica gravada na sala fixa, e encher a lista
              * de todo mundo nao e decisao de convidado.
              */}
            {channels && isModerator && onCreateChannel && (
                <CreateChannel
                    full={channels.length >= MAX_CHANNELS}
                    onCreate={onCreateChannel}
                />
            )}

            {/* Painel estilo Discord no canto inferior esquerdo */}
            <DiscordVoicePanel
                code={code}
                roomName={roomName}
                channelName={canalAtual?.name}
                nickname={self?.nickname ?? ''}
                selfId={selfId}
                avatar={avatar}
                onAvatarChange={onAvatarChange}
                isSpeaking={speaking.has(selfId ?? '')}
                sharingKinds={sharingKinds}
                canShareScreen={canShareScreen}
                canUseCamera={canUseCamera}
                quality={quality}
                resolution={resolution}
                encoder={encoder}
                encoderNote={encoderNote}
                cameraId={cameraId}
                background={background}
                effectLoading={effectLoading}
                inVoice={inVoice}
                muted={muted}
                deafened={deafened}
                screenHasAudio={screenHasAudio}
                screenAudioMuted={screenAudioMuted}
                onToggleScreenAudio={onToggleScreenAudio}
                voiceLoading={voiceLoading}
                micId={micId}
                noiseSuppression={noiseSuppression}
                onToggleShare={onToggleShare}
                onToggleVoice={onToggleVoice}
                onToggleMute={onToggleMute}
                onToggleDeafen={onToggleDeafen}
                onMicChange={onMicChange}
                onCameraChange={onCameraChange}
                onQualityChange={onQualityChange}
                onResolutionChange={onResolutionChange}
                onEncoderChange={onEncoderChange}
                receivePreference={receivePreference}
                onReceivePreferenceChange={onReceivePreferenceChange}
                uploadLimit={uploadLimit}
                onUploadLimitChange={onUploadLimitChange}
                onBackgroundChange={onBackgroundChange}
                onNoiseSuppressionChange={onNoiseSuppressionChange}
                onRename={onRename}
                onLeave={onLeave}
            />
        </aside>
    )
}

function ParticipantRow({
    onWatchScreen,
    participant,
    isSelf,
    selfIsHost,
    selfModerates,
    speaking,
    quality,
    volume,
    onVolumeChange,
    onKick,
    onSetAdmin,
    selfMuted,
    selfDeafened,
    outraSala = false
}: {
    onWatchScreen?: (peerId: string) => void
    participant: Participant
    isSelf: boolean
    selfIsHost: boolean
    selfModerates: boolean
    speaking: boolean
    /** `1080p60 · 4.2 Mbps` — vazia enquanto o video nao chegou. */
    quality?: string
    volume: number
    onVolumeChange: (peerId: string, volume: number) => void
    onKick: (id: string) => void
    onSetAdmin: (id: string, admin: boolean) => void
    selfMuted?: boolean
    selfDeafened?: boolean
    /**
     * Esta numa sub-sala diferente da minha. A voz dela nao chega aqui, entao
     * nao ha volume pra ajustar — o slider so confundiria.
     */
    outraSala?: boolean
}) {
    const naVoz = isInVoice(participant)
    const modera = canModerate(participant)

    /**
     * As mesmas regras do servidor, so que aqui pra nao mostrar botao que ja
     * nasce recusado. Quem decide de verdade continua sendo o servidor: isto
     * e cortesia visual, nao seguranca.
     */
    // Admin nao derruba admin — so o host tira quem tambem modera.
    const canKick = selfModerates && !isSelf && (selfIsHost || !modera)
    const canPromote = selfModerates && !isSelf && !modera
    // Rebaixar e do host; a excecao e o proprio admin abrir mao do cargo.
    const canDemote = !!participant.admin && (selfIsHost || isSelf)
    // Fora da voz o microfone tambem esta fechado: quem acabou de entrar na
    // sala ve o botao de microfone cortado na barra, e a lista nao pode dizer
    // o contrario. So mostrar o icone com `naVoz` fazia ele aparecer apenas
    // depois de ligar e mutar de novo.
    const isMuted = !naVoz || !!(isSelf ? selfMuted : participant.muted)
    const isDeafened = isSelf ? selfDeafened : participant.deafened
    const hasCamera = participant.publications.some((p) => p.kind === 'camera')
    const hasScreen = participant.publications.some((p) => p.kind === 'screen')

    return (
        <li className="group relative flex h-8 items-center gap-2 rounded-md px-2 transition-colors hover:bg-accent/50 select-none">
            {/* Avatar com anel verde ao falar (Estilo Discord) */}
            <div className="relative shrink-0 flex items-center justify-center">
                <span
                    className={cn(
                        'absolute -inset-0.5 rounded-full transition-all duration-150',
                        speaking ? 'ring-2 ring-emerald-500 opacity-100' : 'ring-0 opacity-0'
                    )}
                    aria-hidden
                />
                <Avatar nickname={participant.nickname} avatar={participant.avatar} size={24} />
            </div>

            {/* Nickname & Badges (Estilo Discord em 1 linha limpa) */}
            <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
                <span
                    className={cn(
                        'truncate text-[13px] font-medium transition-colors',
                        speaking
                            ? 'text-emerald-400 font-semibold'
                            : outraSala
                                ? 'text-zinc-500 group-hover:text-zinc-300'
                                : 'text-zinc-300 group-hover:text-foreground'
                    )}
                >
                    {participant.nickname}
                </span>

                {participant.host && (
                    <span title="Host da sala" className="flex items-center">
                        <Crown className="h-3 w-3 shrink-0 text-amber-400" />
                    </span>
                )}

                {participant.admin && !participant.host && (
                    <span title="Moderador da sala" className="flex items-center">
                        <Shield className="h-3 w-3 shrink-0 text-sky-400" />
                    </span>
                )}

                {/* Badge AO VIVO quando transmitindo tela */}
                {hasScreen && (
                    <button
                        type="button"
                        disabled={isSelf || !onWatchScreen}
                        onClick={() => onWatchScreen?.(participant.id)}
                        title={isSelf ? 'Você está transmitindo' : `Assistir ${participant.nickname}`}
                        className="flex items-center gap-0.5 rounded bg-destructive/90 px-1 py-0.2 text-[9px] font-bold uppercase tracking-wider text-white shadow-sm shrink-0 enabled:cursor-pointer enabled:hover:bg-destructive"
                    >
                        <Radio className="h-2.5 w-2.5 animate-pulse" />
                        AO VIVO
                    </button>
                )}

                {/* Badge VÍDEO quando câmera ligada */}
                {hasCamera && !hasScreen && (
                    <span className="flex items-center gap-0.5 rounded bg-primary/80 px-1 py-0.2 text-[9px] font-bold uppercase tracking-wider text-black shadow-sm shrink-0">
                        <Video className="h-2.5 w-2.5" />
                        VÍDEO
                    </span>
                )}

                {/*
                  * Qualidade do que ESTA CHEGANDO, ao lado do AO VIVO.
                  *
                  * Nao e a resolucao que a pessoa escolheu no menu dela: com
                  * simulcast as duas divergem de proposito, e o numero util
                  * aqui e o que explica por que a imagem daquela pessoa esta
                  * pior — ou por que esta pesando.
                  */}
                {quality && (
                    <span
                        title="Qualidade que esta chegando ate voce"
                        className="shrink-0 rounded bg-muted/70 px-1 py-0.2 font-mono text-[9px] font-medium tabular-nums text-muted-foreground"
                    >
                        {quality}
                    </span>
                )}
            </div>

            {/* Ícones de Mudo e Fone Silenciado à Direita (Estilo Discord) */}
            <div className="flex items-center gap-1 shrink-0">
                {isMuted && (
                    <span title="Microfone desativado" className="flex items-center text-zinc-400 group-hover:text-red-400/90 transition-colors">
                        <MicOff className="h-3.5 w-3.5" />
                    </span>
                )}

                {isDeafened && (
                    <span title="Áudio desativado" className="flex items-center text-zinc-400 group-hover:text-red-400/90 transition-colors">
                        <HeadphonesOffIcon className="h-3.5 w-3.5" />
                    </span>
                )}

                {/* Volume Slider no Hover — focus-within tambem, senao quem navega
                    por teclado chega no slider e ele continua invisivel. */}
                {naVoz && !isSelf && !outraSala && (
                    <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center pl-1">
                        <input
                            type="range"
                            min={0}
                            max={1}
                            step={0.05}
                            value={volume}
                            title={`Volume: ${Math.round(volume * 100)}%`}
                            onChange={(event) => onVolumeChange(participant.id, Number(event.target.value))}
                            className="h-1 w-12 cursor-pointer accent-primary"
                        />
                    </div>
                )}

                {/* Promover a moderador */}
                {canPromote && (
                    <button
                        type="button"
                        onClick={() => onSetAdmin(participant.id, true)}
                        title={`Tornar ${participant.nickname} moderador`}
                        className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 p-0.5 text-muted-foreground hover:text-sky-400 transition-all rounded"
                    >
                        <ShieldPlus className="h-3.5 w-3.5" />
                    </button>
                )}

                {/* Rebaixar (ou abrir mao do proprio cargo) */}
                {canDemote && (
                    <button
                        type="button"
                        onClick={() => onSetAdmin(participant.id, false)}
                        title={isSelf ? 'Deixar de ser moderador' : `Tirar a moderação de ${participant.nickname}`}
                        className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 p-0.5 text-sky-400 hover:text-destructive transition-all rounded"
                    >
                        <ShieldMinus className="h-3.5 w-3.5" />
                    </button>
                )}

                {/* Botão Kick — aqui expulsar tambem barra a volta pelo link */}
                {canKick && (
                    <button
                        type="button"
                        onClick={() => onKick(participant.id)}
                        title={`Remover ${participant.nickname} e barrar a volta`}
                        className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 p-0.5 text-muted-foreground hover:text-destructive transition-all rounded"
                    >
                        <UserMinus className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
        </li>
    )
}

/**
 * Participantes agrupados por sub-sala, como os canais de voz do Discord.
 *
 * A sala principal vem primeiro e sempre aparece — e pra onde todo mundo
 * chega. Clicar no cabecalho de uma sub-sala leva a pessoa pra la; e o mesmo
 * gesto que quem usa Discord ja faz sem pensar.
 */
function ChannelList({
    channels,
    participants,
    selfChannel,
    canDelete,
    onJoin,
    onDelete,
    renderRow
}: {
    channels: Channel[]
    participants: Participant[]
    selfChannel: string | null
    canDelete: boolean
    onJoin: (id: string | null) => void
    onDelete: (id: string) => void
    renderRow: (participant: Participant, outraSala?: boolean) => React.ReactNode
}) {
    // Quem esta numa sub-sala que acabou de sumir da lista aparece na principal:
    // e pra la que o servidor esta levando a pessoa, e o `peer-update` que
    // confirma isso chega logo atras.
    const existentes = new Set(channels.map((channel) => channel.id))
    const membrosDe = (id: string | null) => participants.filter((participant) => (
        id === null
            ? !participant.channel || !existentes.has(participant.channel)
            : participant.channel === id
    ))

    return (
        <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto px-1.5 pb-2">
            <ChannelGroup
                name="Sala principal"
                icon={<Home className="h-3.5 w-3.5 shrink-0" />}
                current={selfChannel === null}
                members={membrosDe(null)}
                onJoin={() => onJoin(null)}
                renderRow={renderRow}
            />
            {channels.map((channel) => (
                <ChannelGroup
                    key={channel.id}
                    name={channel.name}
                    icon={<Volume2 className="h-3.5 w-3.5 shrink-0" />}
                    current={selfChannel === channel.id}
                    members={membrosDe(channel.id)}
                    onJoin={() => onJoin(channel.id)}
                    onDelete={canDelete ? () => onDelete(channel.id) : undefined}
                    renderRow={renderRow}
                />
            ))}
        </ul>
    )
}

function ChannelGroup({
    name,
    icon,
    current,
    members,
    onJoin,
    onDelete,
    renderRow
}: {
    name: string
    icon: React.ReactNode
    /** E a sub-sala em que eu estou. */
    current: boolean
    members: Participant[]
    onJoin: () => void
    onDelete?: () => void
    renderRow: (participant: Participant, outraSala?: boolean) => React.ReactNode
}) {
    /**
     * Apagar em dois cliques, como a sala fixa na tela inicial: quem esta la
     * dentro volta pra principal com tudo desligado, e isso nao pode sair de
     * um clique errado no icone ao lado do nome.
     */
    const [confirmando, setConfirmando] = useState(false)

    return (
        <li>
            <div className="group/canal flex items-center gap-1">
                <button
                    type="button"
                    onClick={onJoin}
                    disabled={current}
                    aria-current={current ? 'location' : undefined}
                    aria-label={current ? `${name} (voce esta aqui)` : `Entrar em ${name}`}
                    title={current ? `Voce esta em ${name}` : `Entrar em ${name}`}
                    className={cn(
                        'flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-[12px] font-semibold transition-colors',
                        current
                            ? 'cursor-default bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                    )}
                >
                    {icon}
                    <span className="truncate">{name}</span>
                    {members.length > 0 && (
                        <span className="shrink-0 text-[10px] font-medium opacity-70">{members.length}</span>
                    )}
                    {!current && (
                        <span className="ml-auto shrink-0 text-[10px] font-medium opacity-0 transition-opacity group-hover/canal:opacity-100">
                            Entrar
                        </span>
                    )}
                </button>

                {onDelete && (confirmando ? (
                    <div className="flex shrink-0 items-center gap-0.5">
                        <button
                            type="button"
                            onClick={() => {
                                onDelete()
                                setConfirmando(false)
                            }}
                            title={members.length > 0
                                ? `Apagar ${name} — quem esta la volta pra sala principal`
                                : `Apagar ${name}`}
                            className="flex h-6 items-center rounded-md bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground transition-colors hover:bg-destructive/90"
                        >
                            Apagar?
                        </button>
                        <button
                            type="button"
                            onClick={() => setConfirmando(false)}
                            title="Cancelar"
                            aria-label="Cancelar"
                            className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => setConfirmando(true)}
                        title={`Apagar ${name}`}
                        aria-label={`Apagar ${name}`}
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-all hover:text-destructive focus-visible:opacity-100 group-hover/canal:opacity-100"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                ))}
            </div>

            {members.length > 0 && (
                <ul className="mt-0.5 space-y-0.5 pl-2">
                    {members.map((participant) => renderRow(participant, !current))}
                </ul>
            )}
        </li>
    )
}

/**
 * "Criar sala": recolhido num botao ate alguem precisar, e ai vira o campo do
 * nome ali mesmo — sem janela por cima da sala pra uma coisa de uma palavra.
 */
function CreateChannel({ full, onCreate }: { full: boolean; onCreate: (name: string) => void }) {
    const [aberto, setAberto] = useState(false)
    const [nome, setNome] = useState('')

    const fechar = () => {
        setAberto(false)
        setNome('')
    }

    if (!aberto) {
        return (
            <div className="border-t border-border/70 px-2 py-1.5">
                <button
                    type="button"
                    onClick={() => setAberto(true)}
                    disabled={full}
                    title={full ? `Esta sala ja tem ${MAX_CHANNELS} sub-salas` : 'Criar uma sub-sala'}
                    className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border/70 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary/50 hover:bg-primary/5 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border/70 disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                >
                    <Plus className="h-3.5 w-3.5" />
                    Criar sala
                </button>
            </div>
        )
    }

    return (
        <form
            className="flex items-center gap-1 border-t border-border/70 px-2 py-1.5"
            onSubmit={(event) => {
                event.preventDefault()
                const clean = nome.trim()
                if (!clean) return
                onCreate(clean)
                fechar()
            }}
        >
            <MriInput
                autoFocus
                size="sm"
                value={nome}
                maxLength={MAX_CHANNEL_NAME}
                placeholder="Nome da sala"
                aria-label="Nome da sub-sala"
                onChange={(event) => setNome(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Escape') fechar()
                }}
                className="h-7 text-xs"
            />
            <button
                type="submit"
                disabled={!nome.trim()}
                title="Criar"
                aria-label="Criar"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-primary transition-colors hover:bg-primary/10 disabled:opacity-40"
            >
                <Check className="h-3.5 w-3.5" />
            </button>
            <button
                type="button"
                onClick={fechar}
                title="Cancelar"
                aria-label="Cancelar"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
            >
                <X className="h-3.5 w-3.5" />
            </button>
        </form>
    )
}

/**
 * Quem foi expulso, com o botao de liberar.
 *
 * Fica recolhida por padrao: e uma lista que so importa na hora de desfazer um
 * engano, e ocupar a barra com ela empurraria os participantes pra baixo.
 */
function BannedList({ entries, onUnban }: { entries: BanEntry[]; onUnban: (id: string) => void }) {
    const [open, setOpen] = useState(false)

    return (
        <div className="border-t border-border/70 px-1.5 py-1.5">
            <button
                type="button"
                onClick={() => setOpen((value) => !value)}
                className="flex w-full items-center justify-between rounded-md px-1.5 py-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground"
                aria-expanded={open}
            >
                <span className="flex items-center gap-1.5">
                    <Users className="h-3 w-3" />
                    Expulsos
                </span>
                <span className="rounded-full bg-destructive/20 px-1.5 py-0.2 text-[10px] font-bold text-destructive-foreground/90">
                    {entries.length}
                </span>
            </button>

            {open && (
                <ul className="mt-1 max-h-32 space-y-0.5 overflow-y-auto">
                    {entries.map((entry) => (
                        <li
                            key={entry.id}
                            className="group flex h-7 items-center gap-2 rounded-md px-2 transition-colors hover:bg-accent/50"
                        >
                            <Avatar nickname={entry.nickname} avatar={entry.avatar} size={20} />
                            <span
                                className="min-w-0 flex-1 truncate text-[12px] text-muted-foreground"
                                title={`Removido por ${entry.byNickname}`}
                            >
                                {entry.nickname}
                            </span>
                            <button
                                type="button"
                                onClick={() => onUnban(entry.id)}
                                title={`Liberar ${entry.nickname} pra voltar`}
                                className="p-0.5 text-muted-foreground opacity-0 transition-all group-hover:opacity-100 focus-within:opacity-100 hover:text-emerald-400"
                            >
                                <RotateCcw className="h-3.5 w-3.5" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

function HeadphonesOffIcon({ className }: { className?: string }) {
    return (
        <svg
            className={className}
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3" />
            <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" strokeWidth="2.5" />
        </svg>
    )
}

export { Avatar } from '@/components/Avatar'
